import {MDIcons} from '@mintdata-wsp/common/components/icon/MDIcons';
import {Spinner, SpinnerSize, SpinnerStyle} from '@mintdata-wsp/common/components/spinner/Spinner';
import {ApiError} from '@mintdata-wsp/common/controllers/network/Fetcher';
import {InvitationDetailsResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/InvitationDetailsResponse';
import {User} from '@mintdata-wsp/common/state/DataState';
import {MemberRole, OrganizationMember} from '@mintdata-wsp/common/state/UIState';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {DataLine, DataLineColorType} from '@mintdata-wsp/wsp/ui/components/dataline/DataLine';
import {Column} from '@mintdata-wsp/wsp/ui/components/forms/Column';
import {Row} from '@mintdata-wsp/wsp/ui/components/forms/Row';
import {ButtonStyle, WSPButton} from '@mintdata-wsp/wsp/ui/components/general/WSPButton';
import {MenuItem} from '@mintdata-wsp/wsp/ui/components/menus/MenuItem';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import {action, observable} from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';

const styles = require('./MemberCard.less');
const cx = classnames.bind(styles);

export interface MemberCardProps
{
    className?:string;
    member:OrganizationMember;
    highlight?:string;
    onRemove:(member:OrganizationMember) => void;
    errorMessage:string | null;
    onCloseError:(member:OrganizationMember) => void;
}

class ResendState
{
    @observable inProgress:boolean = false;
    @observable isSent:boolean = false;
    @observable errorMessage:string = null;
}

@observer
export class MemberCard extends React.Component<MemberCardProps, any>
{
    @observable
    private _menuItems:MenuItem[] = [];

    private _errorMessage:string = '';

    @observable
    private _isErrorMessageFade:boolean = false;

    private _resend:ResendState = new ResendState();

    private _timeout:any;

    @action
    componentWillMount()
    {
        const {member} = this.props;

        if( (member.myRole == MemberRole.Admins && !member.isMyProfile) || member.role == MemberRole.Pending )
        {
            this._menuItems = [
                MenuItem.simple('remove', 'Remove from organization', this._removeFromOrganization),
            ];
        }
    }

    componentWillReceiveProps(props)
    {
        const {errorMessage} = props;
        if( errorMessage && this._errorMessage !== errorMessage )
        {
            this._errorMessage = errorMessage;
            this._timeout = setTimeout(this._hideErrorTimeout, 3000);
        }
    }

    @action.bound
    private _hideErrorTimeout()
    {
        this._isErrorMessageFade = true;
        clearTimeout(this._timeout);

        setTimeout(this._onCloseError, 500);
    }

    @action.bound
    private _onCloseError()
    {
        const {member, onCloseError} = this.props;

        this._errorMessage = '';
        this._isErrorMessageFade = false;

        onCloseError(member);
    }

    @action.bound
    private _onResend()
    {
        const {member} = this.props;

        this._resend.inProgress = true;
        this._resend.isSent = false;
        this._resend.errorMessage = null;

        controllers.ui.members.resendInvitation(member.id)
            .then(action((response:InvitationDetailsResponse | ApiError) =>
            {
                if( ApiError.isError(response) )
                {
                    console.warn(response);
                    throw (response as ApiError).message;
                }
                // setTimeout(action(()=>{

                controllers.ui.members.syncOrganizationPendingUsers();
                this._resend.inProgress = false;
                this._resend.isSent = true;

                // }), 4000);
            }))
            .catch(action((message:string | any) =>
            {
                console.warn(message);

                if( message )
                {
                    // setTimeout(action(()=>{
                    this._resend.inProgress = false;
                    this._resend.errorMessage = 'Sending failed. Try again';
                    // }), 4000);
                }
            }));
    }

    @bind
    private _removeFromOrganization()
    {
        const {member, onRemove} = this.props;

        controllers.ui.dialogs.showDangerConfirm(
            'Remove from organization?',
            `User "${member.fullName}"`,
            () =>
            {
                if( onRemove && typeof onRemove == 'function' )
                    onRemove(member);
            },
            ['Cancel', 'REMOVE']
        );
    }

    private _getSpinner():JSX.Element
    {
        return <Spinner size={SpinnerSize.Medium_16} style={SpinnerStyle.Blue} className={cx('resendSpinner')} />;
    }

    @bind
    private _renderResend()
    {
        const {inProgress, isSent, errorMessage} = this._resend;
        return (
            <Row className={cx('pending')}>

                Pending invitation &middot;&nbsp;

                {!isSent && !inProgress && !errorMessage && (
                    <span className={cx('linkResend')} onClick={this._onResend}>Resend</span>
                )}
                {inProgress && (
                    <Row>
                        <span className={cx('resendSending')}>Sending...</span>
                        {this._getSpinner()}
                    </Row>
                )}
                {!!errorMessage && (
                    <Row>
                        <span className={cx('resendError')}>{errorMessage}</span>
                        &nbsp;&middot;&nbsp;
                        <span className={cx('linkResend')} onClick={this._onResend}>Resend</span>
                    </Row>
                )}
                {isSent && (
                    <span className={cx('resendSent')}>
                        <span className={styles.desktopView}>Invitation has been sent</span>
                        <span className={styles.mobileView}>Resent!</span>
                    </span>
                )}
            </Row>
        );
    }

    render()
    {
        const {member, highlight, className, errorMessage} = this.props;

        const isNewLine = false;

        let colorType = DataLineColorType.Green;

        if( member.role === MemberRole.Pending )
            colorType = DataLineColorType.Orange;

        const initials = User.initials({fullName: member.fullName} as User);

        const avatarFontSize = 'fs-' + (16 - (initials.length - 1) * 2);

        let fullName = member.fullName;

        const isErrorMessageFade = this._isErrorMessageFade;

        const isPending = member.role === MemberRole.Pending;

        if( highlight )
        {
            const re = new RegExp(`(${highlight})`, 'i');
            fullName = fullName.replace(re, `<b class="${cx('highlight')}">$1</b>`);
        }

        const memberRole = member.role == MemberRole.Pending
            ? MemberRole[MemberRole.Members]
            : MemberRole[member.role];

        const isMyProfile = member.isMyProfile;

        return (
            <DataLine
                menuItems={this._menuItems}
                isNewLine={isNewLine}
                colorType={colorType}
                className={cx('memberCard', className)}
            >
                {
                    member.avatarUrl
                        ? <img src={member.avatarUrl} className={cx('avatar')} alt={initials} />
                        : <div className={cx('avatar', avatarFontSize)}>{initials}</div>
                }
                <Row className={styles.nameAndRoleWrapper}>
                    <Column className={cx('fullNameWrapper')}>
                        <div
                            className={cx('fullName', {isMyProfile})}
                            dangerouslySetInnerHTML={{__html: fullName}}
                        />
                        {isPending && this._renderResend()}
                    </Column>

                    <Row className={cx('role', {isPending})}>
                        {memberRole}
                        {/*<Icon className={cx('showFilter-icon')}*/}
                        {/*name={Icons.ArrowDropdown}*/}
                        {/*size={IconSize.medium16}*/}
                        {/*/>*/}
                    </Row>
                </Row>

                {/*<div className={cx('round', '-spaces')}>{member.spaces} spaces</div>*/}
                {/*<div className={cx('round', '-envs')}>{member.environments} environments</div>*/}
                {/*<div className={cx('round', '-teams')}>{member.teams} teams</div>*/}

                {errorMessage && (
                    <Row className={cx('error', {isErrorMessageFade})}>

                        <Row className={cx('wrapper')}>
                            <span className={cx('message')}>
                                {errorMessage}
                            </span>
                        </Row>

                        <WSPButton
                            className={cx('createSpaceClose')}
                            icon={MDIcons.common.Close}
                            style={ButtonStyle.SimpleSecondary}
                            onClick={this._hideErrorTimeout}
                        />

                    </Row>
                )}

            </DataLine>
        );
    }
}





